import { Container, Graphics, Sprite } from 'pixi.js';
import { MAP_H, MAP_W, colOf, cellOf } from '../sim/constants';
import {
  FLAG_CABLE,
  FLAG_PAVED,
  FLAG_PIPE,
  FLAG_POWER,
  FLAG_RAIL,
  FLAG_ROAD,
  ORE_GOLD,
  ORE_IRON,
  type Terrain,
} from '../sim/types';
import type { GrayboxTextures } from './graybox';
import { ELEV_STEP, HALF_H, HALF_W, tileTopX, tileTopY } from './iso';
import { DEPTH_TINTS, FACE_TINT_SE, FACE_TINT_SW } from './tint';

export const CHUNK_SIZE = 16;
export const CHUNKS_X = MAP_W / CHUNK_SIZE;
export const CHUNKS_Y = MAP_H / CHUNK_SIZE;

const ROAD_FILL = 0xbfb193;
const ROAD_RUT = 0x8d8066;
const PAVED_FILL = 0xaaa89e;
const PAVED_JOINT = 0x8b897f;
const PIPE_STEEL = 0x4d80a8;
const PIPE_DARK = 0x35607f;
const CABLE_YELLOW = 0xc9a53a;
const CABLE_DARK = 0x8f7526;
const RAIL_TIE = 0x6b4f36;
const RAIL_STEEL_DARK = 0x707580;
const RAIL_STEEL_LIGHT = 0x8a8f98;
const RAIL_BALLAST = 0x9a8c72;
const GRID_CLEARING = 0xb6a98c;
const GRID_STEEL = 0x7d828c;
const GRID_WIRE = 0x4a4a52;

/** Directions as [dx, dy, edge-mid offset from tile top vertex]. */
const DIRECTIONS: readonly (readonly [number, number, number, number])[] = [
  [1, 0, HALF_W / 2, HALF_H * 1.5], // east neighbor across the SE edge
  [-1, 0, -HALF_W / 2, HALF_H * 0.5], // west across the NW edge
  [0, 1, -HALF_W / 2, HALF_H * 1.5], // south across the SW edge
  [0, -1, HALF_W / 2, HALF_H * 0.5], // north across the NE edge
];

function hasSameFlag(terrain: Terrain, x: number, y: number, flag: number): boolean {
  if (x < 0 || x >= MAP_W || y < 0 || y >= MAP_H) return false;
  return ((terrain.flags[colOf(x, y)] ?? 0) & flag) !== 0;
}

/**
 * Rebuilds one 16x16-tile chunk: depth-tinted floors, ore glints, cliff faces
 * with visible ore veins, and connected road/pipe networks drawn as Graphics.
 * Row-major iteration is painter-correct because elevation only goes down.
 */
export function bakeChunk(container: Container, chunkX: number, chunkY: number, terrain: Terrain, tex: GrayboxTextures): void {
  container.removeChildren();
  const x0 = chunkX * CHUNK_SIZE;
  const y0 = chunkY * CHUNK_SIZE;
  for (let ty = y0; ty < y0 + CHUNK_SIZE; ty += 1) {
    for (let tx = x0; tx < x0 + CHUNK_SIZE; tx += 1) {
      const col = colOf(tx, ty);
      const depth = terrain.depth[col] ?? 0;
      const topX = tileTopX(tx, ty);
      const topY = tileTopY(tx, ty, depth);

      const variant = terrain.reclaimed[col]
        ? tex.floorReclaimed
        : tex.floorVariants[(Math.imul(col, 2654435761) >>> 0) % tex.floorVariants.length]!;
      const floor = new Sprite(variant);
      floor.position.set(topX - HALF_W, topY);
      floor.tint = DEPTH_TINTS[depth] ?? 0xffffff;
      container.addChild(floor);

      const nextCell = cellOf(col, depth);
      const nextOre = terrain.oreType[nextCell] ?? 0;
      if (nextOre === ORE_IRON || nextOre === ORE_GOLD) {
        const glint = new Sprite(nextOre === ORE_IRON ? tex.glintIron : tex.glintGold);
        glint.position.set(topX - HALF_W, topY);
        container.addChild(glint);
      }

      // Cliff faces below the SW edge (neighbor x, y+1) and SE edge (x+1, y),
      // with ore veins where this column's buried cells hold ore.
      const addFaces = (facing: 'sw' | 'se', neighborDepth: number): void => {
        for (let level = 0; level < neighborDepth - depth; level += 1) {
          const face = new Sprite(facing === 'sw' ? tex.faceSW : tex.faceSE);
          const fx = facing === 'sw' ? topX - HALF_W : topX;
          const fy = topY + HALF_H + level * ELEV_STEP;
          face.position.set(fx, fy);
          face.tint = (facing === 'sw' ? FACE_TINT_SW : FACE_TINT_SE)[Math.min(7, depth + level + 1)] ?? 0xffffff;
          container.addChild(face);
          const buriedCell = cellOf(col, depth + level);
          const buriedOre = terrain.oreType[buriedCell] ?? 0;
          if (buriedOre === ORE_IRON || buriedOre === ORE_GOLD) {
            const vein = new Sprite(tex.faceOre(facing, buriedOre === ORE_IRON ? 'iron' : 'gold'));
            vein.position.set(fx, fy);
            container.addChild(vein);
          }
        }
      };
      const southDepth = ty + 1 < MAP_H ? (terrain.depth[colOf(tx, ty + 1)] ?? 0) : depth;
      addFaces('sw', southDepth);
      const eastDepth = tx + 1 < MAP_W ? (terrain.depth[colOf(tx + 1, ty)] ?? 0) : depth;
      addFaces('se', eastDepth);
    }
  }

  // Connected road + pipe networks, baked as one Graphics per chunk.
  const overlay = new Graphics();
  let drewAny = false;
  for (let ty = y0; ty < y0 + CHUNK_SIZE; ty += 1) {
    for (let tx = x0; tx < x0 + CHUNK_SIZE; tx += 1) {
      const col = colOf(tx, ty);
      const flags = terrain.flags[col] ?? 0;
      if ((flags & (FLAG_ROAD | FLAG_PIPE | FLAG_CABLE | FLAG_RAIL | FLAG_POWER)) === 0) continue;
      drewAny = true;
      const depth = terrain.depth[col] ?? 0;
      const topX = tileTopX(tx, ty);
      const topY = tileTopY(tx, ty, depth);
      const cx = topX;
      const cy = topY + HALF_H;

      if (flags & FLAG_ROAD) {
        const paved = (flags & FLAG_PAVED) !== 0;
        const fill = paved ? PAVED_FILL : ROAD_FILL;
        for (const [dx, dy, mx, my] of DIRECTIONS) {
          if (!hasSameFlag(terrain, tx + dx, ty + dy, FLAG_ROAD)) continue;
          const ex = topX + mx;
          const ey = topY + my;
          const px = ((cy - ey) / 18) * 5;
          const py = ((ex - cx) / 18) * 5;
          overlay.poly([cx - px, cy - py, ex - px, ey - py, ex + px, ey + py, cx + px, cy + py]).fill(fill);
        }
        overlay.poly([cx, cy - 6, cx + 12, cy, cx, cy + 6, cx - 12, cy]).fill(fill);
        if (paved) {
          // Flagstone joints instead of wheel ruts.
          for (const [dx, dy, mx, my] of DIRECTIONS) {
            if (!hasSameFlag(terrain, tx + dx, ty + dy, FLAG_ROAD)) continue;
            const ex = topX + mx;
            const ey = topY + my;
            for (const f of [0.33, 0.66]) {
              const jx = cx + (ex - cx) * f;
              const jy = cy + (ey - cy) * f;
              overlay.moveTo(jx - 3, jy + 1.5).lineTo(jx + 3, jy - 1.5).stroke({ width: 1, color: PAVED_JOINT, alpha: 0.9 });
            }
          }
          overlay.circle(cx, cy, 1).fill(PAVED_JOINT);
        } else {
          for (const [dx, dy, mx, my] of DIRECTIONS) {
            if (!hasSameFlag(terrain, tx + dx, ty + dy, FLAG_ROAD)) continue;
            const ex = topX + mx;
            const ey = topY + my;
            const px = ((cy - ey) / 18) * 2.2;
            const py = ((ex - cx) / 18) * 2.2;
            overlay.moveTo(cx - px, cy - py).lineTo(ex - px, ey - py).stroke({ width: 1, color: ROAD_RUT, alpha: 0.8 });
            overlay.moveTo(cx + px, cy + py).lineTo(ex + px, ey + py).stroke({ width: 1, color: ROAD_RUT, alpha: 0.8 });
          }
        }
      }

      if (flags & FLAG_PIPE) {
        let connections = 0;
        for (const [dx, dy, mx, my] of DIRECTIONS) {
          if (!hasSameFlag(terrain, tx + dx, ty + dy, FLAG_PIPE)) continue;
          connections += 1;
          const ex = topX + mx;
          const ey = topY + my;
          overlay.moveTo(cx, cy).lineTo(ex, ey).stroke({ width: 3, color: PIPE_STEEL });
          overlay.circle(ex, ey, 1.8).fill(PIPE_DARK);
        }
        if (connections === 0) {
          overlay.moveTo(cx - 5, cy).lineTo(cx + 5, cy).stroke({ width: 3, color: PIPE_STEEL });
        }
        overlay.circle(cx, cy, 2.4).fill(PIPE_DARK);
        overlay.circle(cx - 0.5, cy - 0.5, 0.9).fill(0x8fc0dd);
      }

      // Buried power cable: the yellow twin of the water line, nudged down a
      // few pixels so both can share a trench visibly.
      if (flags & FLAG_CABLE) {
        const oy = flags & FLAG_PIPE ? 4 : 0;
        let connections = 0;
        for (const [dx, dy, mx, my] of DIRECTIONS) {
          if (!hasSameFlag(terrain, tx + dx, ty + dy, FLAG_CABLE)) continue;
          connections += 1;
          const ex = topX + mx;
          const ey = topY + my;
          overlay.moveTo(cx, cy + oy).lineTo(ex, ey + oy).stroke({ width: 3, color: CABLE_YELLOW });
          overlay.circle(ex, ey + oy, 1.8).fill(CABLE_DARK);
        }
        if (connections === 0) {
          overlay.moveTo(cx - 5, cy + oy).lineTo(cx + 5, cy + oy).stroke({ width: 3, color: CABLE_YELLOW });
        }
        overlay.circle(cx, cy + oy, 2.4).fill(CABLE_DARK);
        overlay.circle(cx - 0.5, cy + oy - 0.5, 0.9).fill(0xf2dc8a);
      }

      // Rail right-of-way: track runs west-east edge to edge, baked into the
      // terrain so buildings occlude it correctly and it follows elevation.
      // Drawn after roads, so a road on the same tile reads as a crossing.
      if (flags & FLAG_RAIL) {
        const wx = topX - HALF_W / 2;
        const wy = topY + HALF_H * 0.5;
        const ex2 = topX + HALF_W / 2;
        const ey2 = topY + HALF_H * 1.5;
        // Perpendicular unit for the (2,1) iso axis.
        const pxu = -0.447;
        const pyu = 0.894;
        overlay
          .poly([wx - pxu * 5, wy - pyu * 5, ex2 - pxu * 5, ey2 - pyu * 5, ex2 + pxu * 5, ey2 + pyu * 5, wx + pxu * 5, wy + pyu * 5])
          .fill({ color: RAIL_BALLAST, alpha: 0.5 });
        for (const t of [0.1, 0.3, 0.5, 0.7, 0.9]) {
          const jx = wx + (ex2 - wx) * t;
          const jy = wy + (ey2 - wy) * t;
          overlay
            .moveTo(jx - pxu * 4.5, jy - pyu * 4.5)
            .lineTo(jx + pxu * 4.5, jy + pyu * 4.5)
            .stroke({ width: 2.5, color: RAIL_TIE });
        }
        for (const [offset, color] of [[-2, RAIL_STEEL_LIGHT], [2, RAIL_STEEL_DARK]] as const) {
          overlay
            .moveTo(wx + pxu * offset, wy + pyu * offset)
            .lineTo(ex2 + pxu * offset, ey2 + pyu * offset)
            .stroke({ width: 1.5, color });
        }
      }

      // Transmission right-of-way: a cleared strip with a slack catenary and
      // a lattice pylon every few tiles. Same west-east axis as the rail.
      if (flags & FLAG_POWER) {
        const wx = topX - HALF_W / 2;
        const wy = topY + HALF_H * 0.5;
        const ex2 = topX + HALF_W / 2;
        const ey2 = topY + HALF_H * 1.5;
        const pxu = -0.447;
        const pyu = 0.894;
        overlay
          .poly([wx - pxu * 4, wy - pyu * 4, ex2 - pxu * 4, ey2 - pyu * 4, ex2 + pxu * 4, ey2 + pyu * 4, wx + pxu * 4, wy + pyu * 4])
          .fill({ color: GRID_CLEARING, alpha: 0.45 });
        const pylon = tx % 4 === 0;
        const armY = pylon ? 26 : 22;
        // The wire sags between pylons.
        overlay
          .moveTo(wx, wy - armY + (pylon ? 0 : 2))
          .quadraticCurveTo(topX, topY + HALF_H - armY + 5, ex2, ey2 - armY + (pylon ? 0 : 2))
          .stroke({ width: 1, color: GRID_WIRE });
        if (pylon) {
          const bx = topX;
          const by = topY + HALF_H;
          for (const side of [-1, 1] as const) {
            overlay.moveTo(bx + side * 4, by).lineTo(bx + side * 1.5, by - armY).stroke({ width: 1.5, color: GRID_STEEL });
          }
          for (let level = 0; level < 3; level += 1) {
            const y0 = by - (level * armY) / 3;
            const y1 = by - ((level + 1) * armY) / 3;
            const w0 = 4 - level * 0.85;
            const w1 = 4 - (level + 1) * 0.85;
            overlay.moveTo(bx - w0, y0).lineTo(bx + w1, y1).stroke({ width: 0.8, color: GRID_STEEL });
            overlay.moveTo(bx + w0, y0).lineTo(bx - w1, y1).stroke({ width: 0.8, color: GRID_STEEL });
          }
          overlay.rect(bx - 7, by - armY - 2, 14, 1.5).fill(GRID_STEEL);
        }
      }
    }
  }
  if (drewAny) container.addChild(overlay);
  else overlay.destroy();
}
